import { ChargementImpossible } from '@/components/ChargementImpossible'
import { estIdentiteAgent, libelleCorrespondant, numeroCorrespondant } from '@/lib/identiteAgent'
import { memo, useCallback, useMemo, useState } from 'react'
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso'
import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  Star,
  UserPlus,
  Voicemail as VoicemailIcon,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatHeure } from '@/lib/heure'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import { useFavorites } from '@/api/favorites'
import { useCallLogs } from '@/api/calls'
import { useFirstCompany, useProfile } from '@/api/profile'
import { nomLigne, useInboxLines } from '@/api/inbox'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useEstDesktop } from '@/hooks/useEstDesktop'
import { EnteteColonnes, LigneAppel, useColonnesHistorique } from './LigneAppel'
import { useContactsMap } from '@/hooks/useContactsMap'
import type { CallFilter, CallLog } from '@/types/calls'
import { InlineVoicemailPlayer } from './InlineVoicemailPlayer'

interface Props {
  filter: CallFilter
  onCallClick: (number: string) => void
  /** Optionnel : sans lui, pas de bouton ⓘ (le fil de la conversation suffit). */
  onDetailClick?: (call: CallLog) => void
  onDirectCall: (number: string) => void
  darkMode?: boolean
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '0'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins === 0) return `0:${secs.toString().padStart(2, '0')}`
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatFavPhone(phone: string): string {
  const c = phone.replace(/\D/g, '')
  if (c.length === 11 && c.startsWith('1')) {
    return `+1 (${c.slice(1, 4)}) ${c.slice(4, 7)}-${c.slice(7)}`
  }
  if (c.length === 10) return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6)}`
  return phone
}

type RelativeDateT = (
  key: string,
  optsOrDefault?: string | Record<string, unknown>,
) => string

function formatRelativeDateLabel(dateString: string, t: RelativeDateT): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.round(diffMs / 60_000)
  // Écart en jours de calendrier (minuit à minuit) : un appel d'hier soir
  // n'est jamais « il y a 0 jour ».
  const jour = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diffDays = Math.round((jour(now) - jour(date)) / 86_400_000)

  if (diffMins < 1) return t('history.time.justNow', 'Il y a un instant')
  if (diffMins < 60 && diffDays === 0) {
    const result = t('history.time.minsAgo', { minutes: diffMins })
    return result && result !== 'history.time.minsAgo'
      ? result
      : `Il y a ${diffMins} min`
  }
  if (diffDays <= 0) return t('history.time.today', "Aujourd'hui")
  if (diffDays === 1) return t('history.time.yesterday', 'Hier')
  if (diffDays < 7) {
    const result = t('history.time.daysAgo', { days: diffDays })
    return result && result !== 'history.time.daysAgo'
      ? result
      : `Il y a ${diffDays} jours`
  }
  return date.toLocaleDateString('fr-FR').toUpperCase()
}

function isCallMissed(call: CallLog): boolean {
  return (
    (call.direction === 'inbound' || !!call.is_internal) &&
    ['missed', 'no-answer', 'busy', 'failed', 'canceled'].includes(call.status) &&
    !call.has_voicemail
  )
}

export function CallHistoryList({
  filter,
  onCallClick,
  onDetailClick,
  onDirectCall,
  darkMode = false,
}: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const query = useCallLogs(
    filter === 'keypad' || filter === 'favorites' ? 'all' : filter,
  )
  const { data: company } = useFirstCompany()
  const { getDisplayName, loading: contactsLoading, reloadContacts } = useContactsMap()
  const { favorites, toggleFavorite, isFavorite } = useFavorites()
  // Bureau : rangées compactes en colonnes ; mobile : la liste d'avant.
  const estDesktop = useEstDesktop()
  // Colonnes choisies par l'utilisateur ; Ligne n'a de sens qu'avec plusieurs lignes.
  const [colonnes, changerColonne] = useColonnesHistorique()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile } = useProfile()
  const moiId = profile?.id ?? null
  const lignes = useInboxLines()
  const colonnesVisibles = useMemo(
    () => ({ ...colonnes, ligne: colonnes.ligne && lignes.length > 1 }),
    [colonnes, lignes.length],
  )
  const libellesLignes = useMemo(() => new Map(lignes.map((l) => [l.number, l])), [lignes])
  const ligneDe = (call: CallLog) => {
    if (lignes.length < 2 || !call.ligne) return null
    return nomLigne(libellesLignes.get(call.ligne) ?? { number: call.ligne, label: '' }, formatPhoneDisplay)
  }

  const [expandedVmId, setExpandedVmId] = useState<number | null>(null)
  const [vmErrors] = useState<Set<number>>(new Set())
  const [expandedTranscriptions, setExpandedTranscriptions] = useState<Set<number>>(new Set())

  const [savingCallId, setSavingCallId] = useState<string | null>(null)
  const [savingContactPhone, setSavingContactPhone] = useState<string | null>(null)
  const [contactFirstName, setContactFirstName] = useState('')
  const [contactLastName, setContactLastName] = useState('')
  const [savingContact, setSavingContact] = useState(false)

  const allCalls = query.data?.pages.flatMap((p) => p.calls || []) || []

  const openSaveForm = (callId: string, phone: string) => {
    setSavingCallId(callId)
    setSavingContactPhone(phone)
    setContactFirstName('')
    setContactLastName('')
  }

  const closeSaveForm = () => {
    setSavingCallId(null)
    setSavingContactPhone(null)
  }

  const handleSaveContact = async () => {
    if (!savingContactPhone || (!contactFirstName.trim() && !contactLastName.trim())) return
    try {
      setSavingContact(true)
      await api.put(`/api/contacts/${encodeURIComponent(savingContactPhone)}/`, {
        first_name: contactFirstName.trim(),
        last_name: contactLastName.trim(),
      })
      await reloadContacts()
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      closeSaveForm()
    } catch (err) {
      console.error('Erreur sauvegarde contact:', err)
    } finally {
      setSavingContact(false)
    }
  }

  const openVoicemailPlayer = (vm: CallLog) => {
    if (!company?.id || vmErrors.has(vm.id)) return
    if (expandedVmId === vm.id) {
      setExpandedVmId(null)
      return
    }
    setExpandedVmId(vm.id)
    if (!vm.is_read) {
      // Backend (views.py:1665) attend `{ action: 'mark_read' }`,
      // pas `{ is_read: true }`.
      void api
        .patch(`/api/companies/${company.id}/voicemails/${vm.id}/`, { action: 'mark_read' })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['call-logs'] })
          queryClient.invalidateQueries({ queryKey: ['voicemails', company.id] })
        })
        .catch(() => {})
    }
  }

  // ----------------------------------------------------------
  // Hooks dérivés — DOIVENT être appelés à chaque render (pas après un
  // early return) pour respecter les Rules of Hooks de React.
  // ----------------------------------------------------------

  const handleEndReached = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage()
    }
  }, [query])

  // Groupes consécutifs (ordre conservé). Plus efficace qu'un Record :
  // un seul passage, déjà aplati pour la virtualisation.
  const { groupLabels, groupCounts } = useMemo(() => {
    const labels: string[] = []
    const counts: number[] = []
    let currentLabel = ''
    for (const call of allCalls) {
      const label = formatRelativeDateLabel(
        call.started_at,
        t as unknown as RelativeDateT,
      )
        .split('•')[0]
        .trim()
      if (label !== currentLabel) {
        labels.push(label)
        counts.push(0)
        currentLabel = label
      }
      counts[counts.length - 1] += 1
    }
    return { groupLabels: labels, groupCounts: counts }
  }, [allCalls, t])

  // Handlers stables — passés aux rows mémoïsés. Sans useCallback, React.memo
  // sur les rows serait inefficace (référence handler change à chaque render).
  const handleTogglePlayer = useCallback(
    (call: CallLog) => openVoicemailPlayer(call),
    // openVoicemailPlayer est défini inline plus haut — ses deps changent
    // rarement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedVmId, company?.id, vmErrors],
  )
  const handleToggleTranscription = useCallback((callId: number) => {
    setExpandedTranscriptions((prev) => {
      const next = new Set(prev)
      if (next.has(callId)) next.delete(callId)
      else next.add(callId)
      return next
    })
  }, [])
  const handleCollapsePlayer = useCallback(() => setExpandedVmId(null), [])
  // `isFavorite` et `toggleFavorite` viennent de Zustand persist — leurs
  // références sont stables, pas besoin de useCallback supplémentaire.

  const tokenForAudio = company?.id
    ? useAuthStore.getState().accessToken || ''
    : ''

  // ----------------------------------------------------------
  // Early returns — APRÈS tous les hooks
  // ----------------------------------------------------------

  // Vue « Favoris » : liste des contacts favoris (persistés côté serveur), pas
  // l'historique d'appels. Indépendante du fetch call-logs.
  if (filter === 'favorites') {
    if (favorites.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 h-full">
          <div
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center mb-4',
              darkMode ? 'bg-slate-800' : 'bg-gray-100',
            )}
          >
            <Star className={cn('h-8 w-8', darkMode ? 'text-slate-500' : 'text-gray-400')} />
          </div>
          <h3
            className={cn(
              'text-lg font-medium mb-2',
              darkMode ? 'text-slate-100' : 'text-gray-900',
            )}
          >
            {t('favorites.empty.title', 'Aucun favori')}
          </h3>
          <p
            className={cn(
              'text-sm text-center max-w-xs',
              darkMode ? 'text-slate-400' : 'text-gray-500',
            )}
          >
            {t(
              'favorites.empty.subtitle',
              "Ajoutez des contacts en favoris depuis l'historique d'appels (icône étoile).",
            )}
          </p>
        </div>
      )
    }
    return (
      <div className="overflow-y-auto h-full py-1">
        {favorites.map((fav) => {
          const name = fav.name || getDisplayName(fav.number) || fav.number
          const initial = (name.trim().charAt(0) || '#').toUpperCase()
          return (
            <div
              key={fav.number}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 transition-colors',
                darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-gray-50',
              )}
            >
              <button
                type="button"
                onClick={() => onDirectCall(fav.number)}
                className="flex-1 flex items-center gap-3 text-left min-w-0"
                title={t('favorites.call', 'Appeler')}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                  style={{ backgroundColor: '#23375F' }}
                >
                  {initial.match(/[A-Z0-9]/) ? initial : '#'}
                </div>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium truncate',
                      darkMode ? 'text-slate-100' : 'text-gray-900',
                    )}
                  >
                    {name}
                  </p>
                  <p
                    className={cn(
                      'text-xs truncate',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  >
                    {formatFavPhone(fav.number)}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => onDirectCall(fav.number)}
                className="w-8 h-8 rounded-full bg-green-500/10 hover:bg-green-500/20 flex items-center justify-center shrink-0 transition-colors"
                title={t('favorites.call', 'Appeler')}
              >
                <PhoneCall className="w-4 h-4 text-green-500" />
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(fav.number, name)}
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-amber-500 hover:bg-amber-500/10 transition-colors"
                title={t('favorites.remove', 'Retirer des favoris')}
              >
                <Star className="w-4 h-4 fill-current" />
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  // Loading initial : skeleton de lignes "fantômes" plutôt qu'un empty state
  // trompeur. `contactsLoading` = lookup contacts, `query.isPending` = 1er fetch
  // call-logs. Une fois qu'on a des données, isPending=false même en background.
  if (contactsLoading || query.isPending) {
    return (
      <div className="px-2 pt-3 space-y-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <CallRowSkeleton key={i} darkMode={darkMode} />
        ))}
      </div>
    )
  }

  if (query.isError && allCalls.length === 0) {
    return <ChargementImpossible darkMode={darkMode} />
  }

  if (allCalls.length === 0) {
    const Icon = filter === 'voicemail' ? VoicemailIcon : Phone
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 h-full">
        <div
          className={cn(
            'w-16 h-16 rounded-full flex items-center justify-center mb-4',
            darkMode ? 'bg-slate-800' : 'bg-gray-100',
          )}
        >
          <Icon className={cn('h-8 w-8', darkMode ? 'text-slate-500' : 'text-gray-400')} />
        </div>
        <h3
          className={cn(
            'text-lg font-medium mb-2',
            darkMode ? 'text-slate-100' : 'text-gray-900',
          )}
        >
          {t('history.empty.title', 'Aucun appel')}
        </h3>
        <p
          className={cn(
            'text-sm text-center max-w-xs',
            darkMode ? 'text-slate-400' : 'text-gray-500',
          )}
        >
          {filter === 'missed' && (t('history.empty.missed', 'Aucun appel manqué'))}
          {filter === 'outbound' && (t('history.empty.outbound', 'Aucun appel sortant'))}
          {filter === 'inbound' && (t('history.empty.inbound', 'Aucun appel entrant'))}
          {filter === 'voicemail' && 'Aucun message vocal'}
          {filter === 'ai' &&
            (t('history.empty.ai', 'Aucun appel répondu par l\'IA'))}
          {(filter === 'all' || filter === 'keypad') &&
            (t('history.empty.all', "Pas encore d'appels"))}
        </p>
      </div>
    )
  }

  const piedDeListe = () => (
    <div className="py-4">
      {query.isFetchingNextPage && (
        <div className="flex justify-center items-center gap-2 py-2">
          <Loader2 className={cn('w-4 h-4 animate-spin', darkMode ? 'text-slate-400' : 'text-gray-400')} />
        </div>
      )}
    </div>
  )

  if (estDesktop) {
    return (
      <div className="flex h-full flex-col">
        <EnteteColonnes darkMode={darkMode} avecLigne={lignes.length > 1} colonnes={colonnesVisibles} onChanger={changerColonne} />
        <Virtuoso
          style={{ flex: 1 }}
          data={allCalls}
          endReached={handleEndReached}
          increaseViewportBy={400}
          computeItemKey={(_, call) => `${call.type ?? 'call'}-${call.id}`}
          itemContent={(_, call) => {
            const numero = call.type === 'voicemail' ? call.from_number : numeroCorrespondant(call)
            const saisie = savingCallId === `${call.type === 'voicemail' ? 'vm' : 'call'}-${call.id}`
            return (
              <div>
                <LigneAppel
                  call={call}
                  darkMode={darkMode}
                  moiId={moiId}
                  locale={locale}
                  isFav={isFavorite(numero)}
                  nomLigne={ligneDe(call)}
                  colonnes={colonnesVisibles}
                  getDisplayName={getDisplayName}
                  onOuvrir={onCallClick}
                  onToggleFavorite={toggleFavorite}
                  onEnregistrerContact={openSaveForm}
                />
                {saisie && (
                  <div className="px-3 pb-2">
                    <InlineSaveContact
                      darkMode={darkMode}
                      firstName={contactFirstName}
                      lastName={contactLastName}
                      saving={savingContact}
                      onChangeFirstName={setContactFirstName}
                      onChangeLastName={setContactLastName}
                      onSave={handleSaveContact}
                      onClose={closeSaveForm}
                    />
                  </div>
                )}
              </div>
            )
          }}
          components={{ Footer: piedDeListe }}
        />
      </div>
    )
  }

  return (
    <GroupedVirtuoso
      style={{ height: '100%' }}
      groupCounts={groupCounts}
      endReached={handleEndReached}
      increaseViewportBy={400}
      groupContent={(index) => (
        <div
          className={cn(
            'py-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider',
            darkMode
              ? 'text-slate-500 bg-slate-900'
              : 'text-gray-400 bg-white',
          )}
        >
          {groupLabels[index]}
        </div>
      )}
      itemContent={(index) => {
        const call = allCalls[index]
        if (!call) return null
        if (call.type === 'voicemail') {
          return (
            <VoicemailRow
              call={call}
              darkMode={darkMode}
              expanded={expandedVmId === call.id}
              transcriptionExpanded={expandedTranscriptions.has(call.id)}
              onTogglePlayer={() => handleTogglePlayer(call)}
              onToggleTranscription={() => handleToggleTranscription(call.id)}
              hasError={vmErrors.has(call.id)}
              onCallClick={onCallClick}
              onDirectCall={onDirectCall}
              getDisplayName={getDisplayName}
              audioUrl={
                company?.id
                  ? `${API_BASE_URL}/api/companies/${company.id}/voicemails/${call.id}/audio/?token=${encodeURIComponent(tokenForAudio)}`
                  : ''
              }
              onCollapsePlayer={handleCollapsePlayer}
              savingCallId={savingCallId}
              contactFirstName={contactFirstName}
              contactLastName={contactLastName}
              savingContact={savingContact}
              onOpenSaveForm={openSaveForm}
              onCloseSaveForm={closeSaveForm}
              onChangeFirstName={setContactFirstName}
              onChangeLastName={setContactLastName}
              onSaveContact={handleSaveContact}
            />
          )
        }
        return (
          <CallRow
            call={call}
            darkMode={darkMode}
            isFav={isFavorite(
              numeroCorrespondant(call),
            )}
            onCallClick={onCallClick}
            onDetailClick={onDetailClick}
            onToggleFavorite={toggleFavorite}
            getDisplayName={getDisplayName}
            savingCallId={savingCallId}
            contactFirstName={contactFirstName}
            contactLastName={contactLastName}
            savingContact={savingContact}
            onOpenSaveForm={openSaveForm}
            onCloseSaveForm={closeSaveForm}
            onChangeFirstName={setContactFirstName}
            onChangeLastName={setContactLastName}
            onSaveContact={handleSaveContact}
          />
        )
      }}
      components={{
        Footer: () => (
          <div className="py-4">
            {query.isFetchingNextPage && (
              <div className="flex justify-center items-center gap-2 py-2">
                <Loader2
                  className={cn(
                    'w-4 h-4 animate-spin',
                    darkMode ? 'text-slate-400' : 'text-gray-400',
                  )}
                />
                <span
                  className={cn(
                    'text-xs',
                    darkMode ? 'text-slate-400' : 'text-gray-500',
                  )}
                >
                  Chargement…
                </span>
              </div>
            )}
            {!query.hasNextPage && allCalls.length > 0 && (
              <div
                className={cn(
                  'text-center text-xs font-medium py-3',
                  darkMode ? 'text-slate-600' : 'text-gray-400',
                )}
              >
                - Fin de l&apos;historique -
              </div>
            )}
          </div>
        ),
      }}
    />
  )
}

interface InlineSaveContactProps {
  darkMode: boolean
  firstName: string
  lastName: string
  saving: boolean
  onChangeFirstName: (v: string) => void
  onChangeLastName: (v: string) => void
  onSave: () => void
  onClose: () => void
}

function InlineSaveContact({
  darkMode,
  firstName,
  lastName,
  saving,
  onChangeFirstName,
  onChangeLastName,
  onSave,
  onClose,
}: InlineSaveContactProps) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'flex items-center gap-2 mt-1.5 rounded-lg p-2',
        darkMode ? 'bg-slate-800/50' : 'bg-indigo-50/50',
      )}
    >
      <input
        autoFocus
        placeholder="Prénom"
        value={firstName}
        onChange={(e) => onChangeFirstName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave()
          if (e.key === 'Escape') onClose()
        }}
        className={cn(
          'w-20 text-[12px] px-2 py-1 rounded border outline-none',
          darkMode
            ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500'
            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-indigo-400',
        )}
      />
      <input
        placeholder="Nom"
        value={lastName}
        onChange={(e) => onChangeLastName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave()
          if (e.key === 'Escape') onClose()
        }}
        className={cn(
          'w-20 text-[12px] px-2 py-1 rounded border outline-none',
          darkMode
            ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500'
            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-indigo-400',
        )}
      />
      <button
        type="button"
        onClick={onSave}
        disabled={saving || (!firstName.trim() && !lastName.trim())}
        className={cn(
          'p-1 rounded-full transition-all',
          darkMode
            ? 'text-green-400 hover:bg-green-900/30 disabled:text-slate-600'
            : 'text-green-600 hover:bg-green-50 disabled:text-gray-300',
        )}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onClose}
        className={cn(
          'p-1 rounded-full transition-all',
          darkMode ? 'text-slate-500 hover:bg-slate-800' : 'text-gray-400 hover:bg-gray-100',
        )}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

interface VoicemailRowProps {
  call: CallLog
  darkMode: boolean
  expanded: boolean
  transcriptionExpanded: boolean
  hasError: boolean
  audioUrl: string
  onTogglePlayer: () => void
  onToggleTranscription: () => void
  onCallClick: (number: string) => void
  onDirectCall: (number: string) => void
  getDisplayName: (phoneNumber: string | null | undefined) => string
  onCollapsePlayer: () => void
  savingCallId: string | null
  contactFirstName: string
  contactLastName: string
  savingContact: boolean
  onOpenSaveForm: (callId: string, phone: string) => void
  onCloseSaveForm: () => void
  onChangeFirstName: (v: string) => void
  onChangeLastName: (v: string) => void
  onSaveContact: () => void
}

const VoicemailRow = memo(function VoicemailRow({
  call,
  darkMode,
  expanded,
  transcriptionExpanded,
  hasError,
  audioUrl,
  onTogglePlayer,
  onToggleTranscription,
  onCallClick,
  onDirectCall,
  getDisplayName,
  onCollapsePlayer,
  savingCallId,
  contactFirstName,
  contactLastName,
  savingContact,
  onOpenSaveForm,
  onCloseSaveForm,
  onChangeFirstName,
  onChangeLastName,
  onSaveContact,
}: VoicemailRowProps) {
  const { i18n } = useTranslation()
  const phoneNumber = call.from_number
  const displayName = getDisplayName(phoneNumber)
  const vmTime = formatHeure(call.started_at || call.created_at || '', i18n.language)
  const durationSecs = call.recording_duration || 0
  const durationLabel =
    durationSecs >= 60
      ? `${Math.floor(durationSecs / 60)}m ${durationSecs % 60}s`
      : `${durationSecs}s`
  const transcriptionIsLong = (call.transcription?.length || 0) > 60
  const isUnknown = displayName === phoneNumber
  const isSaving = savingCallId === `vm-${call.id}`

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 px-3 py-2.5 transition-colors group',
        darkMode ? 'hover:bg-slate-900/50' : 'hover:bg-gray-50/60',
        !call.is_read && (darkMode ? 'bg-orange-950/10' : 'bg-orange-50/40'),
      )}
    >
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-500 relative mt-0.5">
        <VoicemailIcon className="w-3.5 h-3.5 text-white" />
        {!call.is_read && (
          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-950" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => onCallClick(phoneNumber)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3
                className={cn(
                  'text-[13px] font-medium truncate',
                  darkMode ? 'text-white' : 'text-gray-900',
                )}
              >
                {displayName}
              </h3>
              <span
                className={cn(
                  'text-[9px] font-semibold px-1.5 py-px rounded-full',
                  darkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700',
                )}
              >
                Message vocal
              </span>
            </div>

            {call.transcription && (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  if (transcriptionIsLong) onToggleTranscription()
                }}
                className={cn('mt-0.5', transcriptionIsLong && 'cursor-pointer')}
              >
                <p
                  className={cn(
                    'text-[11px] select-text',
                    transcriptionExpanded ? 'whitespace-pre-wrap' : 'truncate',
                    darkMode ? 'text-slate-400' : 'text-gray-500',
                  )}
                >
                  {call.transcription}
                </p>
                {transcriptionIsLong && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-0.5 text-[10px] mt-0.5 font-medium transition-colors',
                      darkMode
                        ? 'text-orange-400/70 hover:text-orange-300'
                        : 'text-orange-500/70 hover:text-orange-600',
                    )}
                  >
                    {transcriptionExpanded ? (
                      <>
                        Réduire <ChevronUp className="w-2.5 h-2.5" />
                      </>
                    ) : (
                      <>
                        Voir plus <ChevronDown className="w-2.5 h-2.5" />
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span
              className={cn(
                'text-[10px] font-medium tabular-nums',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            >
              {durationLabel}
            </span>
            <span
              className={cn(
                'text-[10px] font-medium tabular-nums',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            >
              {vmTime}
            </span>
            {isUnknown && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenSaveForm(`vm-${call.id}`, phoneNumber)
                }}
                className={cn(
                  'p-1 rounded-full transition-all flex-shrink-0 opacity-0 group-hover:opacity-100',
                  darkMode
                    ? 'text-slate-500 hover:text-indigo-400 hover:bg-slate-800'
                    : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50',
                )}
                title="Enregistrer comme contact"
              >
                <UserPlus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {isSaving && (
          <InlineSaveContact
            darkMode={darkMode}
            firstName={contactFirstName}
            lastName={contactLastName}
            saving={savingContact}
            onChangeFirstName={onChangeFirstName}
            onChangeLastName={onChangeLastName}
            onSave={onSaveContact}
            onClose={onCloseSaveForm}
          />
        )}

        {hasError ? (
          <div
            className={cn(
              'flex items-center gap-1.5 mt-1.5 text-[10px]',
              darkMode ? 'text-red-400/80' : 'text-red-500/80',
            )}
          >
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span>Audio indisponible</span>
            <button
              type="button"
              onClick={() => onDirectCall(phoneNumber)}
              className={cn(
                'ml-auto p-1.5 rounded-full transition-all flex-shrink-0',
                darkMode
                  ? 'text-green-500 hover:text-green-400 hover:bg-green-900/30'
                  : 'text-green-600 hover:text-green-700 hover:bg-green-50',
              )}
              title="Rappeler"
            >
              <Phone className="w-3 h-3" />
            </button>
          </div>
        ) : expanded && audioUrl ? (
          <InlineVoicemailPlayer
            audioUrl={audioUrl}
            darkMode={darkMode}
            onCollapse={onCollapsePlayer}
            onCall={() => onDirectCall(phoneNumber)}
          />
        ) : (
          <div className="flex items-center gap-2 mt-1.5">
            <button
              type="button"
              onClick={onTogglePlayer}
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                darkMode
                  ? 'bg-slate-800 text-slate-300 hover:bg-orange-500/20 hover:text-orange-400'
                  : 'bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-orange-600',
              )}
            >
              <Play className="w-3 h-3 ml-0.5" />
            </button>
            <button
              type="button"
              onClick={() => onDirectCall(phoneNumber)}
              className={cn(
                'p-1.5 rounded-full transition-all flex-shrink-0',
                darkMode
                  ? 'text-green-500 hover:text-green-400 hover:bg-green-900/30'
                  : 'text-green-600 hover:text-green-700 hover:bg-green-50',
              )}
              title="Rappeler"
            >
              <Phone className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

interface CallRowProps {
  call: CallLog
  darkMode: boolean
  isFav: boolean
  onCallClick: (number: string) => void
  /** Optionnel : sans lui, pas de bouton ⓘ (le fil de la conversation suffit). */
  onDetailClick?: (call: CallLog) => void
  onToggleFavorite: (number: string, name: string) => void
  getDisplayName: (phoneNumber: string | null | undefined) => string
  savingCallId: string | null
  contactFirstName: string
  contactLastName: string
  savingContact: boolean
  onOpenSaveForm: (callId: string, phone: string) => void
  onCloseSaveForm: () => void
  onChangeFirstName: (v: string) => void
  onChangeLastName: (v: string) => void
  onSaveContact: () => void
}

const CallRow = memo(function CallRow({
  call,
  darkMode,
  isFav,
  onCallClick,
  onDetailClick,
  onToggleFavorite,
  getDisplayName,
  savingCallId,
  contactFirstName,
  contactLastName,
  savingContact,
  onOpenSaveForm,
  onCloseSaveForm,
  onChangeFirstName,
  onChangeLastName,
  onSaveContact,
}: CallRowProps) {
  const { t, i18n } = useTranslation()
  const isOutbound = call.direction === 'outbound'
  const missed = isCallMissed(call)
  const phoneNumber = numeroCorrespondant(call)
  const isInternal = call.is_internal || estIdentiteAgent(phoneNumber)
  const displayName = libelleCorrespondant(call, getDisplayName, t('history.agent', 'Agent'))

  const isUnknown = !isInternal && displayName === phoneNumber
  const callTime = formatHeure(call.started_at, i18n.language)
  const isSaving = savingCallId === `call-${call.id}`

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 transition-colors group',
        darkMode ? 'hover:bg-slate-900/50' : 'hover:bg-gray-50/60',
      )}
    >
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          missed && 'bg-red-500',
          !missed && isOutbound && 'bg-green-500',
        )}
        style={!missed && !isOutbound ? { backgroundColor: '#23375F' } : undefined}
      >
        {missed ? (
          <PhoneCall className="w-3.5 h-3.5 text-white" />
        ) : isOutbound ? (
          <PhoneOutgoing className="w-3.5 h-3.5 text-white" />
        ) : (
          <PhoneIncoming className="w-3.5 h-3.5 text-white" />
        )}
      </div>

      <div
        className={cn('flex-1 min-w-0', isInternal ? 'cursor-default' : 'cursor-pointer')}
        onClick={() => !isInternal && onCallClick(phoneNumber)}
        title={isInternal ? t('history.useTeamTab', 'Utilisez le tab Équipe') : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3
              className={cn(
                'text-[13px] font-medium truncate',
                darkMode ? 'text-slate-100' : 'text-gray-900',
              )}
            >
              {displayName}
            </h3>
            <div className="flex items-center gap-1.5">
              {isInternal && (
                <span
                  className={cn(
                    'text-[9px] font-medium px-1.5 py-px rounded-full',
                    darkMode
                      ? 'bg-purple-900/30 text-purple-300'
                      : 'bg-purple-100 text-purple-700',
                  )}
                >
                  {t('history.internalCall', 'Appel interne')}
                </span>
              )}
              {!isInternal && displayName !== phoneNumber && (
                <span
                  className={cn(
                    'text-[11px]',
                    darkMode ? 'text-slate-500' : 'text-gray-400',
                  )}
                >
                  {phoneNumber}
                </span>
              )}
              {call.answer_type === 'ai' && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-px rounded-full',
                    darkMode
                      ? 'bg-violet-500/15 text-violet-300'
                      : 'bg-violet-50 text-violet-700',
                  )}
                >
                  <Bot className="w-2.5 h-2.5" />
                  {call.agent_name
                    ? t('history.answeredByAi', 'Répondu par {{name}}', {
                        name: call.agent_name,
                      })
                    : t('history.answeredByAiNoName', 'Répondu par l\'IA')}
                </span>
              )}
              {missed && (
                <span
                  className={cn(
                    'text-[11px] font-medium',
                    darkMode ? 'text-red-400' : 'text-red-600',
                  )}
                >
                  {t('history.missed', 'Manqué')}
                </span>
              )}
              {!missed && call.status === 'completed' && (
                <span
                  className={cn(
                    'text-[11px]',
                    darkMode ? 'text-slate-500' : 'text-gray-400',
                  )}
                >
                  {call.duration ? formatDuration(call.duration) : '0'}
                </span>
              )}
              {call.pending_tasks_count != null && call.pending_tasks_count > 0 && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold',
                    darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700',
                  )}
                >
                  {call.pending_tasks_count}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'text-[10px] font-medium flex-shrink-0 tabular-nums',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            >
              {callTime}
            </span>

            {isUnknown && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenSaveForm(`call-${call.id}`, phoneNumber)
                }}
                className={cn(
                  'p-1 rounded-full transition-all flex-shrink-0 opacity-0 group-hover:opacity-100',
                  darkMode
                    ? 'text-slate-500 hover:text-indigo-400 hover:bg-slate-800'
                    : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50',
                )}
                title="Enregistrer comme contact"
              >
                <UserPlus className="w-3.5 h-3.5" />
              </button>
            )}

            {!isInternal && onDetailClick && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onDetailClick(call)
                }}
                className={cn(
                  'p-1 rounded-full transition-all flex-shrink-0 opacity-0 group-hover:opacity-100',
                  darkMode
                    ? 'text-slate-500 hover:text-indigo-400 hover:bg-slate-800'
                    : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50',
                )}
                title="Détail de l'appel"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleFavorite(phoneNumber, displayName)
              }}
              className={cn(
                'p-1 rounded-full transition-all flex-shrink-0',
                // Favori → étoile jaune toujours visible (indicateur de statut).
                // Sinon → masquée, révélée au survol comme les autres actions
                // secondaires (enregistrer contact / détail), pour ne pas
                // encombrer chaque ligne d'un astérisque inutile.
                isFav
                  ? darkMode
                    ? 'text-yellow-400 hover:bg-slate-800'
                    : 'text-yellow-500 hover:bg-gray-100'
                  : cn(
                      'opacity-0 group-hover:opacity-100',
                      darkMode
                        ? 'text-slate-600 hover:text-yellow-400 hover:bg-slate-800'
                        : 'text-gray-300 hover:text-yellow-500 hover:bg-gray-100',
                    ),
              )}
              title={
                isFav
                  ? t('history.removeFromFavorites', 'Retirer des favoris')
                  : t('history.addToFavorites', 'Ajouter aux favoris')
              }
            >
              <Star className={cn('w-3.5 h-3.5 transition-all', isFav && 'fill-current')} />
            </button>
          </div>
        </div>

        {isSaving && (
          <InlineSaveContact
            darkMode={darkMode}
            firstName={contactFirstName}
            lastName={contactLastName}
            saving={savingContact}
            onChangeFirstName={onChangeFirstName}
            onChangeLastName={onChangeLastName}
            onSave={onSaveContact}
            onClose={onCloseSaveForm}
          />
        )}
      </div>
    </div>
  )
})

function CallRowSkeleton({ darkMode }: { darkMode: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg animate-pulse",
        darkMode ? "bg-slate-800/40" : "bg-slate-100/60",
      )}
    >
      <div
        className={cn(
          "h-9 w-9 rounded-full flex-shrink-0",
          darkMode ? "bg-slate-700" : "bg-slate-200",
        )}
      />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div
          className={cn(
            "h-3 rounded w-1/2",
            darkMode ? "bg-slate-700" : "bg-slate-200",
          )}
        />
        <div
          className={cn(
            "h-2.5 rounded w-1/3",
            darkMode ? "bg-slate-800" : "bg-slate-200/70",
          )}
        />
      </div>
      <div
        className={cn(
          "h-2.5 w-12 rounded flex-shrink-0",
          darkMode ? "bg-slate-800" : "bg-slate-200/70",
        )}
      />
    </div>
  )
}
